import { SEO_DESCRIPTION_DEFAULT_FALLBACK, SEO_TITLE_DEFAULT_FALLBACK } from 'mk/autogenerated/translations/Page.a4946595406ffe57be48e4250ce84033'
import { ZoneTargeting } from 'mk/ox';
import { FACEBOOK_APP_ID } from 'mk/settings';
import BackBtn from 'mk2/components/BackBtn';
import { AppName } from 'mk2/containers/Application/Application';
import { cacheLast } from 'mk2/helpers/cache';
import { mainImageUrlWithFallback } from 'mk2/helpers/structuredData';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { AppState } from 'mk2/reducers';
import { MenuSelection } from 'mk2/schemas';
import { Store } from 'mk2/store/configureStore';
import * as React from 'react';
import { MapStateToPropsParam, MergeProps } from 'react-redux';

export interface BrandingProps {
    zone: string;
    key?: string;
    targeting?: ZoneTargeting;
}
export interface HalfpageProps {
    zone: string;
    key?: string;
    targeting?: ZoneTargeting;
}

/**
 * TODO: Add function for enter animation
 * TODO: Add function for leave animation
 * TODO: Can we somehow simplyfy:
 *  type Props = TOwnProps & TStateProps & TDispatchProps & RouteComponentProps<TRouteParams> ?
 *
 * WARN: Please, ... please ... consult all changes you want to make in this file
 */
export default abstract class Page<TOwnProps = {}, TStateProps = {}, TDispatchProps = {}, TMergedProps = TOwnProps & TStateProps & TDispatchProps> {

    protected _imported = {};

    private cacheMenuSelection = cacheLast();
    private cacheHeaderLeft = cacheLast();

    /**
     * Return mapStateToProps configuration for react-redux connect decorator
     */
    public abstract mapStateToProps(): MapStateToPropsParam<TStateProps, TOwnProps, AppState>;

    /**
     * Return mapDispatchToProps configuration for react-redux connect decorator
     */
    public abstract mapDispatchToProps(): MapDispatchToPropsObject<TDispatchProps>;

    /**
     * Return mergeProps configuration for react-redux connect decorator
     */
    public mergeProps(): (
        MergeProps<TStateProps, undefined, TOwnProps, TMergedProps>
        | MergeProps<undefined, TDispatchProps, TOwnProps, TMergedProps>
        | MergeProps<TStateProps, TDispatchProps, TOwnProps, TMergedProps>
        | null
        | undefined
    ) {
        return null;
    }

    /**
     * Permission (hociktoru z vrateneho pola), ktoru musi mat requestUser, aby sa mu Page zobrazila
     */
    public getPermissionRequired(props: TMergedProps): string | string[] | boolean {
        return null;
    }

    /**
     * Return SEO page title for <title> and <meta property="og:title">
     */
    public abstract getMetaTitle(props: TMergedProps): string;

    /**
     * Return SEO page description for <meta property="description"> and <meta property="og:description">
     */
    public getMetaDescription(props: TMergedProps): string {
        return null;
    }

    /**
     * Return JSON object for Schema.org structured data
     */
    public getStructuredData(props: TMergedProps): object[] {
        return null;
    }

    /**
     * Return JSON object for Facebook Open Graph data
     */
    public getOpenGraphData(props: TMergedProps, baseUrl: string, currentUrl: string): {[key: string]: string} {
        // https://developers.facebook.com/docs/sharing/opengraph/object-properties#standard
        return {
            'og:type': 'website',
            'og:title': this.getMetaTitle(props) || SEO_TITLE_DEFAULT_FALLBACK,
            'og:description': this.getMetaDescription(props) || SEO_DESCRIPTION_DEFAULT_FALLBACK,
            'og:image': mainImageUrlWithFallback(undefined),
            'og:url': baseUrl + currentUrl,
            'fb:app_id': FACEBOOK_APP_ID,
        };
    }

    /**
     * Return SEO page description for <meta property="description"> and <meta property="og:description">
     */
    public getMetaRobots(props: TMergedProps): string {
        return 'index,follow';
    }

    /**
     * Return true, if page width should expand to window width (default is to be fixed at 980px)
     */
    public hasFullWidthOnDesktop(props: TMergedProps): boolean {
        return false;
    }

    /**
     * Return props for branding ad
     */
    public getBrandingProps(props: TMergedProps): (BrandingProps | null) {
        return null;
    }

    /**
     * Return props for halfpage ad
     */
    public getHalfpageProps(props: TMergedProps): (HalfpageProps | null) {
        return null;
    }

    /**
     * Render main content of the page
     */
    public abstract render(props: TMergedProps): (JSX.Element | null | false);

    /**
     * Render (optional) right column of the page
     */
    public renderRight(props: TMergedProps): (JSX.Element | null) {
        return null;
    }

    /**
     * Get title for the header
     */
    public getHeaderTitle(props: TMergedProps): (React.ReactNode | null) {
        return null;
    }

    /**
     * Get tabs for the header
     */
    public getHeaderTabs(props: TMergedProps): (JSX.Element[] | null) {
        return null;
    }

    /**
     * Get header left part - by default it is controller by hasHeaderBack() button
     */
    public getHeaderLeft(props: TMergedProps): React.ReactNode {
        if (this.hasHeaderBackButton(props)) {
            const backUrl = this.getDefaultBackPath(props);

            return this.cacheHeaderLeft((
                <BackBtn
                    icon="mkicon-arrow-back@3x.png"
                    link={backUrl}
                    asAnchor={backUrl.startsWith('http')/* back link ide mimo pwa */}
                />
            ), backUrl);
        }

        return null;
    }

    /**
     * Get header right part
     */
    public getHeaderRight(props: TMergedProps): React.ReactNode {
        return null;
    }

    /**
     * Get content footer
     */
    public getFooter(props: TMergedProps): React.ReactNode {
        return null;
    }

    /**
     * Should header be sticky?
     *
     * NOTE: Header can be sticky only on mobile
     */
    public isHeaderSticky(props: TMergedProps): boolean {
        return true;
    }

    /**
     * Return true if you want to show MK logo in the center of mobile Header (if theres no title text)
     */
    public hasMobileLogo(props: TMergedProps): boolean {
        return false;
    }

    /**
     * Should header have a back button?
     */
    public abstract hasHeaderBackButton(props: TMergedProps): boolean;

    /**
     * Default back path when no history exists
     */
    public getDefaultBackPath(props: TMergedProps): (string | null) {
        if (this.hasHeaderBackButton(props)) {
            throw new Error('Page: You must specify default back path for back button if hasHeaderBackButton() returns true');
        }

        return null;
    }

    /**
     * Return false if you do not want to display blue MK header
     */
    public hasPageHeaderPart1(props: TMergedProps): boolean {
        return true;
    }

    /**
     * Return false if you do not want to display header with
     * back button, title and action button
     */
    public hasPageHeaderPart2(props: TMergedProps): boolean {
        return true;
    }

    public getHeaderMenuSelection(app: AppName, props: TMergedProps): MenuSelection {
        // Do not always create new object because SiteMenu is then always re-rendered
        return this.cacheMenuSelection({
            activeSection: app,
            activeItem: undefined,
        }, app);
    }

    /**
     * Return false if you do not want to display blue MK footer
     */
    public hasPageFooter(props: TMergedProps): boolean {
        return true;
    }

    /**
     * Use this to register reducers or sagas
     *
     * @param {MKStore<AppState>} store Can be undefined -> used for preloading
     * @returns {Promise<any> | null}
     */
    public registerInStore(store?: Store<AppState>): Promise<any> | null {
        return null;
    }

    /**
     * Helper to register reducer and saga
     *
     * @param store
     * @param {string} key
     * @param reducerModuleImport
     * @param sagaModuleImport
     */
    protected loadAndRegisterReducerAndSaga(key: string, reducerModuleImport: Promise<any>, sagaModuleImport: Promise<any>, store: Store<AppState>) {
        if (this._imported[key]) {
            if (store) {
                // Page can have no reducer
                if (this._imported[key][0]) {
                    store.injectReducer(key, this._imported[key][0]);
                }
                // Page can have no saga
                if (this._imported[key][1]) {
                    store.injectSaga(key, this._imported[key][1]);
                }
            }
        } else {
            return Promise.all([
                reducerModuleImport,
                sagaModuleImport,
            ]).then((modules) => {
                this._imported[key] = modules.map((m) => m && m.default);
                if (store) {
                    // Page can have no reducer
                    if (this._imported[key][0]) {
                        store.injectReducer(key, this._imported[key][0]);
                    }
                    // Page can have no saga
                    if (this._imported[key][1]) {
                        store.injectSaga(key, this._imported[key][1]);
                    }
                }
            });
        }
    }

}
